// Offline field store for the iPad: event packages (roster + previous results) and captures made with
// no connection, kept in IndexedDB and ENCRYPTED with a key derived from a 6-digit field PIN
// (PBKDF2-SHA-256 → AES-256-GCM, non-extractable, held in memory only while unlocked). This sits on
// top of the iPad's own device encryption. The store locks after 15 minutes idle; five wrong PINs
// lock it for 5 minutes. Unsynced captures are never removed automatically.
import { useSyncExternalStore } from 'react';
import type { AssessmentResult } from '@ngf/calc-engine';
import type { Measurements } from '../components/Intake.tsx';

export const IDLE_LOCK_MS = 15 * 60_000;
export const PACKAGE_DAYS = 14;
const ITERATIONS = 600_000;
const MAX_FAILURES = 5;
const LOCKOUT_MS = 5 * 60_000;

// ---------------------------------------------------------------- types
export interface RosterPerson {
  id: string; firstName: string; lastName: string; dob: string | null; sex: 'M' | 'F' | null; employeeId: string | null;
  assessmentId: string | null; assessmentStatus: string | null;
  orgName?: string | null; makeup?: boolean;   // the organization they test with here (packages from 2026-10-20 on)
  previous: { assessedOn: string; measurements: Measurements; results: AssessmentResult | null } | null;
}
export interface EventPackage {
  event: { id: string; departmentName: string; startsOn: string; endsOn: string | null; location: string | null;
    organizations?: { id: string; name: string; host: boolean }[] };
  roster: RosterPerson[];
  downloadedAt: string;
}
export type SyncState = 'pending' | 'synced' | 'conflict' | 'error';
export interface Capture {
  clientId: string; eventId: string;
  participantId?: string; participantClientId?: string;   // roster person, or a walk-in made on this iPad
  name: string; assessedOn: string; measurements: Measurements;
  capturedAt: string; capturedBy: string;
  state: SyncState; serverId?: string; error?: string; syncedAt?: string;
}
export interface Walkin {
  clientId: string; eventId: string; firstName: string; lastName: string; dob: string | null; sex: 'M' | 'F' | null;
  employeeId: string | null; createdAt: string; state: SyncState; serverId?: string; error?: string;
  departmentId?: string | null;   // which of the event's organizations (several at one event); none = the host
}
interface LockRecord { salt: Uint8Array; iv: Uint8Array; verifier: ArrayBuffer; setBy: string; failures: number; lockedUntil: number }
interface Sealed { iv: Uint8Array; data: ArrayBuffer }
interface EventRow { eventId: string; downloadedAt: string; expiresAt: string; sealed: Sealed }
interface ItemRow { clientId: string; eventId: string; pending: 0 | 1; sealed: Sealed }

// ---------------------------------------------------------------- IndexedDB (no library)
let dbp: Promise<IDBDatabase> | null = null;
function idb(): Promise<IDBDatabase> {
  dbp ??= new Promise((ok, fail) => {
    const r = indexedDB.open('ngf-field', 1);
    r.onupgradeneeded = () => {
      const db = r.result;
      db.createObjectStore('kv');
      db.createObjectStore('events', { keyPath: 'eventId' });
      db.createObjectStore('captures', { keyPath: 'clientId' }).createIndex('event', 'eventId');
      db.createObjectStore('walkins', { keyPath: 'clientId' }).createIndex('event', 'eventId');
    };
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fail(r.error);
  });
  return dbp;
}
type StoreName = 'kv' | 'events' | 'captures' | 'walkins';
async function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T> {
  const db = await idb();
  return new Promise((ok, fail) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    t.oncomplete = () => ok(req ? req.result : (undefined as T));
    t.onerror = () => fail(t.error);
    t.onabort = () => fail(t.error);
  });
}
const getAll = <T>(store: StoreName) => tx<T[]>(store, 'readonly', (s) => s.getAll() as IDBRequest<T[]>);

// ---------------------------------------------------------------- crypto
const enc = new TextEncoder(), dec = new TextDecoder();
async function deriveKey(pin: string, salt: Uint8Array) {
  const base = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', salt: salt as BufferSource, iterations: ITERATIONS, hash: 'SHA-256' }, base,
    { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
async function seal(key: CryptoKey, value: unknown): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  return { iv, data: await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(JSON.stringify(value))) };
}
async function open<T>(key: CryptoKey, s: Sealed): Promise<T> {
  return JSON.parse(dec.decode(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: s.iv as BufferSource }, key, s.data))) as T;
}
const VERIFIER = 'ngf-field-v1';

// ---------------------------------------------------------------- state (useSyncExternalStore)
interface State { ready: boolean; hasPin: boolean; unlocked: boolean; setBy: string | null; version: number }
let state: State = { ready: false, hasPin: false, unlocked: false, setBy: null, version: 0 };
let key: CryptoKey | null = null;
let lastActive = Date.now();
const listeners = new Set<() => void>();
const emit = (patch: Partial<State> = {}) => { state = { ...state, ...patch, version: state.version + 1 }; listeners.forEach((l) => l()); };
export const useField = () => useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, () => state);

export async function init() {
  if (state.ready) return;
  const lock = await tx<LockRecord | undefined>('kv', 'readonly', (s) => s.get('lock') as IDBRequest<LockRecord | undefined>);
  emit({ ready: true, hasPin: !!lock, setBy: lock?.setBy ?? null });
  void navigator.storage?.persist?.();   // ask the browser not to evict (installed home-screen apps are kept anyway)
}

export function touch() { lastActive = Date.now(); }
export function lock() { key = null; if (state.unlocked) emit({ unlocked: false }); }
function needKey() { if (!key) throw new Error('Field mode is locked'); touch(); return key; }

// Lock after 15 idle minutes, including time spent in the background.
if (typeof window !== 'undefined') {
  setInterval(() => { if (key && Date.now() - lastActive > IDLE_LOCK_MS) lock(); }, 30_000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible' && key && Date.now() - lastActive > IDLE_LOCK_MS) lock(); });
  for (const e of ['pointerdown', 'keydown']) window.addEventListener(e, () => { if (key) touch(); }, { passive: true });
}

export const validPin = (pin: string) => /^\d{6}$/.test(pin);

export async function setPin(pin: string, setBy: string) {
  if (!validPin(pin)) throw new Error('The PIN must be 6 digits.');
  if (state.hasPin) throw new Error('A field PIN is already set on this iPad.');
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const k = await deriveKey(pin, salt);
  const v = await seal(k, VERIFIER);
  await tx('kv', 'readwrite', (s) => s.put({ salt, iv: v.iv, verifier: v.data, setBy, failures: 0, lockedUntil: 0 } satisfies LockRecord, 'lock'));
  key = k; touch();
  emit({ hasPin: true, unlocked: true, setBy });
}

export async function unlock(pin: string) {
  const rec = await tx<LockRecord | undefined>('kv', 'readonly', (s) => s.get('lock') as IDBRequest<LockRecord | undefined>);
  if (!rec) throw new Error('No field PIN is set on this iPad.');
  if (rec.lockedUntil > Date.now()) {
    throw new Error(`Too many wrong PINs. Try again in ${Math.ceil((rec.lockedUntil - Date.now()) / 60_000)} minute(s).`);
  }
  const k = await deriveKey(pin, rec.salt);
  try {
    if ((await open<string>(k, { iv: rec.iv, data: rec.verifier })) !== VERIFIER) throw new Error();
  } catch {
    const failures = rec.failures + 1;
    const lockedUntil = failures >= MAX_FAILURES ? Date.now() + LOCKOUT_MS : 0;
    await tx('kv', 'readwrite', (s) => s.put({ ...rec, failures: lockedUntil ? 0 : failures, lockedUntil }, 'lock'));
    throw new Error(lockedUntil ? 'Too many wrong PINs. Field mode is locked for 5 minutes.' : `Wrong PIN (${MAX_FAILURES - failures} tries left).`);
  }
  if (rec.failures) await tx('kv', 'readwrite', (s) => s.put({ ...rec, failures: 0 }, 'lock'));
  key = k; touch();
  emit({ unlocked: true });
  await purgeExpired();
}

/** Forgotten PIN: the only way out is to erase everything on this iPad (unreadable without the PIN anyway). */
export async function eraseAll() {
  for (const s of ['kv', 'events', 'captures', 'walkins'] as const) await tx(s, 'readwrite', (st) => st.clear());
  key = null;
  emit({ hasPin: false, unlocked: false, setBy: null });
}

// ---------------------------------------------------------------- event packages
export async function saveEventPackage(pkg: EventPackage) {
  const k = needKey();
  const expiresAt = new Date(Date.now() + PACKAGE_DAYS * 86_400_000).toISOString();
  const row: EventRow = { eventId: pkg.event.id, downloadedAt: pkg.downloadedAt, expiresAt, sealed: await seal(k, pkg) };
  await tx('events', 'readwrite', (s) => s.put(row));
  emit();
}

export async function listEvents(): Promise<(EventPackage & { expiresAt: string })[]> {
  const k = needKey();
  const rows = await getAll<EventRow>('events');
  const out = await Promise.all(rows.map(async (r) => ({ ...(await open<EventPackage>(k, r.sealed)), expiresAt: r.expiresAt })));
  return out.sort((a, b) => a.event.startsOn.localeCompare(b.event.startsOn));
}

export async function getEvent(eventId: string) {
  const k = needKey();
  const r = await tx<EventRow | undefined>('events', 'readonly', (s) => s.get(eventId) as IDBRequest<EventRow | undefined>);
  return r ? { ...(await open<EventPackage>(k, r.sealed)), expiresAt: r.expiresAt } : null;
}

export async function removeEvent(eventId: string) {
  const pending = (await pendingCount(eventId));
  if (pending) throw new Error(`${pending} capture(s) for this event have not been synced yet.`);
  await tx('events', 'readwrite', (s) => s.delete(eventId));
  for (const store of ['captures', 'walkins'] as const) {
    const rows = await getAll<ItemRow>(store);
    for (const r of rows.filter((x) => x.eventId === eventId)) await tx(store, 'readwrite', (s) => s.delete(r.clientId));
  }
  emit();
}

/** Expired packages go, with their synced captures; anything unsynced keeps its event on the iPad. */
export async function purgeExpired() {
  const rows = await getAll<EventRow>('events');
  for (const r of rows) if (r.expiresAt < new Date().toISOString() && !(await pendingCount(r.eventId))) await removeEvent(r.eventId);
}

// ---------------------------------------------------------------- captures and walk-ins
async function putItem(store: 'captures' | 'walkins', item: Capture | Walkin) {
  const row: ItemRow = { clientId: item.clientId, eventId: item.eventId, pending: item.state === 'synced' ? 0 : 1, sealed: await seal(needKey(), item) };
  await tx(store, 'readwrite', (s) => s.put(row));
  emit();
}
async function listItems<T>(store: 'captures' | 'walkins', eventId?: string): Promise<T[]> {
  const k = needKey();
  const rows = (await getAll<ItemRow>(store)).filter((r) => !eventId || r.eventId === eventId);
  return Promise.all(rows.map((r) => open<T>(k, r.sealed)));
}
export const saveCapture = (c: Capture) => putItem('captures', c);
export const saveWalkin = (w: Walkin) => putItem('walkins', w);
export const listCaptures = (eventId?: string) => listItems<Capture>('captures', eventId);
export const listWalkins = (eventId?: string) => listItems<Walkin>('walkins', eventId);

/** Unsynced items (readable without the PIN: only the counts). */
export async function pendingCount(eventId?: string) {
  let n = 0;
  for (const store of ['captures', 'walkins'] as const) {
    n += (await getAll<ItemRow>(store)).filter((r) => r.pending && (!eventId || r.eventId === eventId)).length;
  }
  return n;
}

// ---------------------------------------------------------------- sync
export class NeedsSignIn extends Error {}
export class Offline extends Error {}
interface SyncResult { clientId: string; ok: boolean; id?: string; outcome?: string; error?: string; conflict?: boolean }

async function post(items: unknown[]): Promise<SyncResult[]> {
  let res: Response;
  try {
    res = await fetch('/api/field/sync', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ items }) });
  } catch { throw new Offline('No connection.'); }
  if (res.status === 401 || res.status === 403) throw new NeedsSignIn('Sign in to sync.');
  if (!res.ok) throw new Error(`Sync failed (${res.status}).`);
  return ((await res.json()) as { results: SyncResult[] }).results;
}

let syncing: Promise<{ synced: number; failed: number }> | null = null;
/** Send every unsynced walk-in (first) and capture. Safe to call repeatedly; the server de-duplicates. */
export function syncNow() {
  syncing ??= (async () => {
    needKey();
    let synced = 0, failed = 0;
    const walkins = (await listWalkins()).filter((w) => w.state !== 'synced');
    const captures = (await listCaptures()).filter((c) => c.state === 'pending' || c.state === 'error');
    const batches: (Walkin | Capture)[][] = [];
    for (let i = 0; i < walkins.length; i += 50) batches.push(walkins.slice(i, i + 50));
    for (let i = 0; i < captures.length; i += 50) batches.push(captures.slice(i, i + 50));
    for (const batch of batches) {
      const results = await post(batch.map((x) => ('measurements' in x
        ? { kind: 'assessment', clientId: x.clientId, eventId: x.eventId, participantId: x.participantId, participantClientId: x.participantClientId,
            assessedOn: x.assessedOn, measurements: x.measurements, capturedAt: x.capturedAt }
        : { kind: 'walkin', clientId: x.clientId, eventId: x.eventId, firstName: x.firstName, lastName: x.lastName, dob: x.dob, sex: x.sex, employeeId: x.employeeId, ...(x.departmentId ? { departmentId: x.departmentId } : {}) })));
      for (const x of batch) {
        const r = results.find((y) => y.clientId === x.clientId);
        const upd = r?.ok
          ? { ...x, state: 'synced' as const, serverId: r.id, error: undefined, syncedAt: new Date().toISOString() }
          : { ...x, state: (r?.conflict ? 'conflict' : 'error') as SyncState, error: r?.error ?? 'No response for this item' };
        if (r?.ok) synced++; else failed++;
        await putItem('measurements' in x ? 'captures' : 'walkins', upd as Capture & Walkin);
      }
    }
    return { synced, failed };
  })().finally(() => { syncing = null; });
  return syncing;
}
